import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import { Box, Card, Grid, IconButton, Typography } from '@mui/material';
import React from 'react';
import { formatDate, formatUserName } from '../../utils/formatUtils'; // Import from shared utils

/**
 * List item component for displaying folders in list view
 * 
 * @param {Object} props Component props
 * @param {Object} props.folder The folder object to display
 * @param {Function} props.onFolderClick Handler for when the folder is clicked
 * @param {Function} props.onMenuOpen Handler for when the context menu is opened
 * @param {boolean} props.showPath Whether to display the folder path (for search results)
 */
const FolderListItem = ({ folder, onFolderClick, onMenuOpen, showPath }) => {
    const handleClick = () => {
        if (onFolderClick) {
            onFolderClick(folder);
        }
    };

    const handleMenuClick = (event) => {
        // Add debug logging
        console.log("Menu button clicked for:", folder.name);
        
        // Important: Stop propagation to prevent folder click
        event.stopPropagation();
        event.preventDefault();
        
        if (onMenuOpen) {
            onMenuOpen(event, folder);
        } else {
            console.error("onMenuOpen not provided to FolderListItem");
        }
    };

    

    // Get accurate item count
    const determineItemCount = () => {
        // First check for explicit itemCount property
        if (typeof folder.itemCount === 'number') {
            return folder.itemCount;
        }
        
        // Then check children array
        if (Array.isArray(folder.children)) {
            return folder.children.length;
        }
        
        // If nothing is available, return 0
        return 0;
    };
    
    // Extract and format the parent path for display
    const displayPath = React.useMemo(() => {
        if (!showPath || !folder.path) return null;
        
        // Format the path for display - don't remove the last part
        // This matches how FileListItem handles paths
        const pathParts = folder.path.split('/').filter(Boolean);
        return '/' + pathParts.join('/');
    }, [folder.path, showPath]);
    
    const itemCount = determineItemCount();

    // Get last updated by (if available)
    const updatedBy = folder.updatedBy || 'Unknown';
    const updatedAt = folder.updatedAt || new Date().toISOString();

    return (
        <Card 
            sx={{ 
                mb: 0, // Remove bottom margin as we're using gap in parent
                p: 1, // Increase padding from 1 to 1.5
                cursor: 'pointer',
                transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                }
            }}
            onClick={handleClick}
            className="folder-list-item"
        >
            <Grid 
                container 
                className='file-folder-item'
                spacing={2} 
                alignItems="center" 
                sx={{ 
                    px: 1, 
                    py: 0.25 
                }}
            >
                <Grid item xs={12} sm={3} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box className="file-folder-name" sx={{ display: 'flex', alignItems: 'center' }}>
                        <FolderIcon color="primary" sx={{ mr: 2, fontSize: '2rem' }} />
                        <Typography variant="body1" noWrap>{folder.name}</Typography>
                    </Box>
                    
                    {/* Display path in search results */}
                    {showPath && displayPath && (
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            noWrap
                            sx={{ ml: 5, mt: 0.5 }}
                        >
                            <FolderOpenIcon 
                                sx={{ fontSize: '0.8rem', mr: 0.5, verticalAlign: 'text-bottom' }} 
                            />
                            {displayPath}
                        </Typography>
                    )}
                </Grid>
                
                <Grid item xs={6} sm={3} md={2}>
                    <Box className="folder-file-details" sx={{ display: 'flex', alignItems: 'center' }}>
                        <FolderZipIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                            {itemCount} item{itemCount !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={6} sm={2} md={1}>
                    <Box className="file-folder-duration" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            -
                        </Typography>
                    </Box>
                </Grid>
                
                <Grid item xs={6} sm={2} md={2}>
                    <Box className="file-folder-updated-by"    sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon 
                                                     
                            sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} 
                        />
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {formatUserName(updatedBy)}
                        </Typography>
                    </Box>
                </Grid>
                
                <Grid item xs={6} sm={2} md={2}>
                    <Box className="file-folder-updated-at"   sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon 
                                                      
                            sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} 
                        />
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {formatDate(updatedAt)}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={6} sm={2} md={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                            className="file-folder-action-menu-button" 
                            size="small"
                            onClick={handleMenuClick}
                            data-testid={`folder-menu-${folder.name}`}
                            aria-label="more options"
                            sx={{
                                opacity: { xs: 1, sm: 0.5 }, // More visible on mobile
                                visibility: 'visible',
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.08)'
                                }
                            }}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Grid>
            </Grid>
        </Card>
    );
};

export default FolderListItem;